"use client";

import { useEffect, useState } from "react";
import { useRouter } from "next/navigation";
import { useUser } from "@/hooks/use-async";
import { subscriptionAPI } from "@/lib/api";
import { MainLayout } from "@/components/layout";
import { Subscription, UserSubscription } from "@/types";

export default function SubscriptionsPage() {
  const router = useRouter();
  const { user } = useUser();
  const [subscriptions, setSubscriptions] = useState<Subscription[]>([]);
  const [activeSubscription, setActiveSubscription] =
    useState<UserSubscription | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [subscribing, setSubscribing] = useState<number | null>(null);

  useEffect(() => {
    if (!user) {
      router.push("/login");
      return;
    }

    const fetchSubscriptions = async () => {
      try {
        const subs = await subscriptionAPI.getSubscriptions();
        setSubscriptions(subs);

        try {
          const active = await subscriptionAPI.getActiveSubscription();
          setActiveSubscription(active);
        } catch {
          // No active subscription
        }
      } catch (err: any) {
        setError(err.message || "Failed to load subscriptions");
      } finally {
        setLoading(false);
      }
    };

    fetchSubscriptions();
  }, [user, router]);

  const handleSubscribe = async (subscriptionId: number) => {
    setSubscribing(subscriptionId);
    try {
      const result = await subscriptionAPI.subscribe(subscriptionId);
      setActiveSubscription(result);
      alert("Successfully upgraded subscription!");
    } catch (err: any) {
      alert(err.message || "Failed to subscribe");
    } finally {
      setSubscribing(null);
    }
  };

  if (loading) {
    return (
      <MainLayout>
        <div className="text-center py-12">Loading...</div>
      </MainLayout>
    );
  }

  return (
    <MainLayout>
      <div className="mb-8">
        <h1 className="text-3xl font-bold mb-2">Subscription Plans</h1>
        <p className="text-gray-600">
          Choose the plan that best fits your needs
        </p>
      </div>

      {error && (
        <div className="mb-6 p-4 bg-red-100 text-red-700 rounded">{error}</div>
      )}

      {activeSubscription && (
        <div className="mb-8 p-4 bg-green-100 text-green-800 rounded-lg">
          <p className="font-semibold">
            Current Plan: {activeSubscription.subscription.name}
          </p>
          <p className="text-sm">
            Max Products: {activeSubscription.subscription.max_products || "∞"}
          </p>
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        {subscriptions.map((subscription) => (
          <div
            key={subscription.id}
            className={`nc-box-has-hover p-6 text-center transition-all rounded-lg ${
              activeSubscription?.subscription_id === subscription.id
                ? "border-green-600 bg-green-50"
                : ""
            }`}
          >
            <h3 className="text-2xl font-bold mb-2">{subscription.name}</h3>
            <p className="text-3xl font-bold text-green-600 mb-2">
              ${subscription.price}
              <span className="text-sm text-gray-600">/month</span>
            </p>

            <div className="mb-6 py-4 border-y">
              <p className="font-semibold text-lg">
                {subscription.max_products === null
                  ? "∞ Products"
                  : `${subscription.max_products} Products`}
              </p>
            </div>

            <p className="text-gray-600 text-sm mb-6">
              {subscription.description}
            </p>

            {activeSubscription?.subscription_id === subscription.id ? (
              <button
                disabled
                className="w-full bg-green-600 text-white py-2 rounded cursor-not-allowed"
              >
                Current Plan
              </button>
            ) : (
              <button
                onClick={() => handleSubscribe(subscription.id)}
                disabled={subscribing === subscription.id}
                className="w-full bg-blue-600 text-white py-2 rounded hover:bg-blue-700 disabled:opacity-50"
              >
                {subscribing === subscription.id
                  ? "Subscribing..."
                  : "Subscribe"}
              </button>
            )}
          </div>
        ))}
      </div>

      <div className="mt-12 p-6 bg-blue-50 rounded-lg">
        <h3 className="font-semibold mb-2">Plan Features:</h3>
        <ul className="text-sm text-gray-700 space-y-1">
          <li>✓ All plans include product management</li>
          <li>✓ Unlimited product ratings and reviews</li>
          <li>✓ Product media management</li>
          <li>✓ Customer support</li>
        </ul>
      </div>
    </MainLayout>
  );
}
